<template>
  <div v-if="hidden" class="flex h-full flex-col items-center gap-2.5 pt-3">
    <span class="inline-flex h-[18px] min-w-[20px] items-center justify-center rounded-full bg-amber-600 px-1.5 text-[10px] font-bold text-white">{{ items.length }}</span>
    <span class="text-[11px] font-bold text-amber-900 [writing-mode:vertical-rl] rotate-180">To be scheduled</span>
  </div>
  <div v-else class="flex h-full min-h-0 flex-col gap-1.5 p-2.5">
    <v-progress-linear v-if="initialLoading" indeterminate color="warning" />
    <v-alert v-else-if="error" type="error" density="compact" variant="tonal">{{ error }}</v-alert>
    <p v-else-if="items.length === 0" class="m-0 px-1 py-5 text-center text-xs text-gray-400">Nothing waiting to be scheduled for {{ vendorCode }}.</p>
    <template v-else>
      <!-- Sits above the list's own scroller, so it stays put while the cards scroll under it. -->
      <input
        id="to-schedule-queue-search"
        v-model="search"
        type="search"
        placeholder="Search PO number"
        aria-label="Search the to-be-scheduled queue"
        class="w-full shrink-0 rounded border border-amber-200 bg-white px-2 py-1 text-[11px] text-gray-900 placeholder:text-gray-400 focus:border-amber-500 focus:outline-none"
      />
      <v-progress-linear v-if="loading" indeterminate color="warning" height="2" />
      <p v-if="visible.length === 0" class="m-0 px-1 py-5 text-center text-xs text-gray-400">No PO matches “{{ search.trim() }}”.</p>
      <div v-else class="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-0.5">
        <ToScheduleQueueCard
          v-for="{ po, index } in visible"
          :key="po.poNumber"
          :po="po"
          :color="cardColor(po, index)"
          :can-write="canWrite"
          @schedule="emit('schedule', po.poNumber)"
          @drag-start="emit('dragStart', po.poNumber)"
          @drag-end="emit('dragEnd')"
        />
      </div>
    </template>
    <div v-if="queueStore.marked.length > 0" class="flex min-h-0 shrink-0 flex-col gap-1.5">
      <label class="flex cursor-pointer items-center gap-1.5 text-[11px] text-amber-900">
        <input type="checkbox" :checked="queueStore.showMarked" @change="queueStore.setShowMarked(($event.target as HTMLInputElement).checked)" />
        Show marked ({{ queueStore.marked.length }})
      </label>
      <template v-if="queueStore.showMarked">
        <v-alert v-if="unmarkError" type="error" density="compact" variant="tonal">{{ unmarkError }}</v-alert>
        <div class="flex max-h-48 flex-col gap-1.5 overflow-y-auto p-0.5">
          <div v-for="po in queueStore.marked" :key="po.poNumber" class="rounded border border-gray-200 bg-gray-100 px-2 py-1.5 text-gray-500">
            <div class="flex items-center justify-between gap-2">
              <span class="text-[11px] font-semibold">{{ po.poNumber }}</span>
              <button v-if="canWrite" type="button" class="text-[11px] font-semibold text-blue-700 hover:underline" @click="unmark(po.poNumber)">Unmark</button>
            </div>
            <div class="text-[10px]">{{ po.markedBy }} · {{ markedOnLabel(po.markedAt) }}</div>
            <div v-if="po.note" class="text-[10px]">{{ po.note }}</div>
          </div>
        </div>
      </template>
    </div>
    <p class="m-0 text-[10px]" :class="syncError || refreshError ? 'font-semibold text-amber-800' : 'text-amber-900'">
      <template v-if="refreshError">
        {{ refreshError }} ·
        <button type="button" class="font-semibold text-blue-700 hover:underline" @click="emit('refresh')">Refresh</button>
      </template>
      <template v-else-if="syncError">
        Couldn't refresh — showing data from {{ updatedAt ?? '—' }} ·
        <button type="button" class="font-semibold text-blue-700 hover:underline" @click="emit('refresh')">Refresh</button>
      </template>
      <template v-else>
        {{ canWrite ? 'Newest order first · click to schedule · ' : '' }}Updated {{ updatedAt ?? '—' }} ·
        <button type="button" class="font-semibold text-blue-700 hover:underline" @click="emit('refresh')">Refresh</button>
      </template>
    </p>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';

  import ToScheduleQueueCard from '@/components/scheduling/ToScheduleQueueCard.vue';
  import { useCalendarPrivacyStore } from '@/store/calendarPrivacyStore';
  import { useCustomerMappingStore } from '@/store/customerMappingStore';
  import { usePoQueueStore } from '@/store/poQueueStore';
  import type { PurchaseOrder } from '@/types/purchaseOrders';
  import { extractErrorMessage } from '@/utils/apiError';
  import { resolvePoSegmentColor } from '@/utils/customerColors';
  import { formatUpdatedAt } from '@/utils/dates';
  import { markedOnLabel } from '@/utils/toScheduleQueue';

  const props = defineProps<{
    items: PurchaseOrder[];
    loading: boolean;
    initialLoading: boolean;
    error: string | null;
    syncedAt: string | null;
    syncError: string | null;
    refreshError: string | null;
    hidden: boolean;
    canWrite: boolean;
    vendorCode: string;
  }>();
  const emit = defineEmits<{ schedule: [poNumber: string]; refresh: []; dragStart: [poNumber: string]; dragEnd: [] }>();

  const queueStore = usePoQueueStore();
  const mappingStore = useCustomerMappingStore();
  const privacyStore = useCalendarPrivacyStore();

  const updatedAt = computed(() => (props.syncedAt ? formatUpdatedAt(props.syncedAt) : null));

  const unmarkError = ref<string | null>(null);

  async function unmark(poNumber: string): Promise<void> {
    unmarkError.value = null;
    try {
      await queueStore.unmark(poNumber);
    } catch (unmarkCatch) {
      unmarkError.value = extractErrorMessage(unmarkCatch, `Could not unmark ${poNumber}`);
    }
  }

  const search = ref('');
  // A search is about one vendor's backlog; carrying it over to the next tab would hide that
  // vendor's queue behind a filter the reader never typed for it.
  watch(
    () => props.vendorCode,
    () => {
      search.value = '';
      unmarkError.value = null;
    }
  );

  // Keeps each card's position in the full list so the partner palette (indexed) does not
  // reshuffle colours as the filter narrows.
  const visible = computed(() => {
    const needle = search.value.trim().toLowerCase();
    return props.items.map((po, index) => ({ po, index })).filter(({ po }) => !needle || po.poNumber.toLowerCase().includes(needle));
  });

  // Same stripe rule as the schedule cards, so a PO keeps its colour from queue to week.
  function cardColor(po: PurchaseOrder, index: number): string {
    return resolvePoSegmentColor(po.poNumber, mappingStore.items, {
      hideCustomerMappings: privacyStore.hideCustomerMappings,
      index,
    });
  }
</script>
